import { isValidHistoryDate } from './historyImport.js'
import { HISTORY_MAX_UNKNOWN_LISTENS } from './historyLimits.js'
import { latestCalendarDate } from './utils/calendarDate.js'

export const RELEASE_DATA_MAX_BYTES = 1024 * 1024
export const RELEASE_DATA_MAX_ROWS = 10_000
export const RELEASE_DATA_HEADER =
	'Discogs ID,listening dates,unknown-date listens,favorite,hidden,hidden until'
export interface ReleaseDataEntry {
	releaseId: number
	dates?: string[]
	unknownCount?: number
	favorite?: boolean
	hidden?: boolean
	hiddenUntil?: string | null
}

export interface ReleaseDataSummary {
	totalRows: number
	releaseCount: number
	datedCount: number
	unknownCount: number
	duplicateDatedCount: number
	importedDatedCount: number
	importedUnknownCount: number
	favoriteCount: number
	hiddenCount: number
	skippedFavoriteCount: number
}

export class ReleaseDataError extends Error {
	constructor(
		message: string,
		readonly row?: number,
	) {
		super(message)
	}
}

function parseRows(csv: string): string[][] {
	const rows: string[][] = []
	let row: string[] = []
	let value = ''
	let quoted = false
	let closed = false
	for (let i = 0; i < csv.length; i++) {
		const char = csv[i]
		if (quoted) {
			if (char === '"' && csv[i + 1] === '"') {
				value += '"'
				i++
			} else if (char === '"') {
				quoted = false
				closed = true
			} else value += char
		} else if (char === ',' || char === '\n' || char === '\r') {
			row.push(value.trim())
			value = ''
			closed = false
			if (char !== ',') {
				if (char === '\r' && csv[i + 1] === '\n') i++
				rows.push(row)
				row = []
			}
		} else if (char === '"' && value === '' && !closed) quoted = true
		else {
			if (closed || char === '"')
				throw new ReleaseDataError(
					'Invalid CSV quoting.',
					rows.length + 1,
				)
			value += char
		}
	}
	if (quoted)
		throw new ReleaseDataError('The CSV contains an unclosed quote.')
	if (value || row.length || closed) rows.push([...row, value.trim()])
	return rows
}

function parseDate(value: string, row: number, future = false): string {
	const calendar = value.slice(0, 10)
	if (
		!isValidHistoryDate(calendar) ||
		!(
			value.length === 10 ||
			/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(value)
		) ||
		!Number.isFinite(Date.parse(value))
	) {
		throw new ReleaseDataError(
			'Dates must be YYYY-MM-DD or UTC ISO timestamps.',
			row,
		)
	}
	const normalized = new Date(value).toISOString()
	if (normalized.slice(0, 10) !== calendar)
		throw new ReleaseDataError('Invalid date.', row)
	if (!future && calendar > latestCalendarDate()) {
		throw new ReleaseDataError(
			'Listening dates cannot be in the future.',
			row,
		)
	}
	return normalized
}

/** Both the browser and server validate the same format; blank cells never clear data. */
export function parseReleaseDataCsv(csv: string): ReleaseDataEntry[] {
	if (new TextEncoder().encode(csv).length > RELEASE_DATA_MAX_BYTES)
		throw new ReleaseDataError('The CSV file must be 1 MB or smaller.')
	const rows = parseRows(csv.replace(/^\uFEFF/, ''))
	const header = rows.shift()?.map((value) => value.toLowerCase())
	const allowed = RELEASE_DATA_HEADER.toLowerCase().split(',')
	const legacy = header?.join(',') === 'discogs id,date'
	if (
		!header ||
		!header.includes('discogs id') ||
		new Set(header).size !== header.length ||
		(!legacy && header.some((value) => !allowed.includes(value)))
	) {
		throw new ReleaseDataError(
			`Use columns: ${RELEASE_DATA_HEADER}. Ratings and Discogs fields are not imported.`,
			1,
		)
	}
	const entries = new Map<number, ReleaseDataEntry>()
	let count = 0
	rows.forEach((columns, index) => {
		if (columns.every((value) => value === '')) return
		const row = index + 2
		if (++count > RELEASE_DATA_MAX_ROWS)
			throw new ReleaseDataError(
				'The CSV exceeds the 10,000 row limit.',
				row,
			)
		if (columns.length !== header.length)
			throw new ReleaseDataError(
				'The number of columns must match the header.',
				row,
			)
		const get = (name: string) => columns[header.indexOf(name)] ?? ''
		const id = get('discogs id')
		const releaseId = Number(id)
		if (
			!/^\d+$/.test(id) ||
			!Number.isSafeInteger(releaseId) ||
			releaseId <= 0
		)
			throw new ReleaseDataError(
				'Discogs ID must be a positive integer.',
				row,
			)
		if (!legacy && entries.has(releaseId))
			throw new ReleaseDataError(
				'Each release must appear in only one row.',
				row,
			)
		const entry: ReleaseDataEntry = legacy
			? (entries.get(releaseId) ?? {
					releaseId,
					dates: [],
					unknownCount: 0,
				})
			: { releaseId }
		if (legacy) {
			const date = get('date')
			if (date) entry.dates?.push(parseDate(date, row))
			else entry.unknownCount = (entry.unknownCount ?? 0) + 1
		} else {
			if (get('listening dates'))
				entry.dates = get('listening dates')
					.split(';')
					.map((date) => parseDate(date.trim(), row))
			const unknown = get('unknown-date listens')
			if (unknown) {
				if (!/^\d+$/.test(unknown))
					throw new ReleaseDataError(
						'Unknown-date listens must be a non-negative integer.',
						row,
					)
				entry.unknownCount = Number(unknown)
			}
			for (const field of ['favorite', 'hidden'] as const) {
				const value = get(field).toLowerCase()
				if (!value) continue
				if (value !== 'true' && value !== 'false')
					throw new ReleaseDataError(
						`${field} must be true, false, or blank.`,
						row,
					)
				entry[field] = value === 'true'
			}
			const until = get('hidden until')
			if (until)
				entry.hiddenUntil =
					until.toLowerCase() === 'forever'
						? null
						: parseDate(until, row, true)
			if (entry.hidden === false && until)
				throw new ReleaseDataError(
					'A visible release cannot have a hidden-until value.',
					row,
				)
		}
		if (
			entry.unknownCount !== undefined &&
			(!Number.isSafeInteger(entry.unknownCount) ||
				entry.unknownCount > HISTORY_MAX_UNKNOWN_LISTENS)
		)
			throw new ReleaseDataError(
				`Unknown-date listens cannot exceed ${HISTORY_MAX_UNKNOWN_LISTENS}.`,
				row,
			)
		entries.set(releaseId, entry)
	})
	if (!entries.size)
		throw new ReleaseDataError('The CSV contains no release rows.')
	return [...entries.values()]
}

/** Merge snapshots by daily listen counts, keeping existing timestamps and repeated listens. */
export function mergeReleaseDates(
	existing: Date[],
	incoming: string[],
): Date[] {
	const remaining = new Map<string, number>()
	for (const date of existing) {
		const day = date.toISOString().slice(0, 10)
		remaining.set(day, (remaining.get(day) ?? 0) + 1)
	}
	return incoming.flatMap((value) => {
		const day = value.slice(0, 10)
		const count = remaining.get(day) ?? 0
		if (count > 0) {
			remaining.set(day, count - 1)
			return []
		}
		return [new Date(value)]
	})
}

export function serializeReleaseData(entries: ReleaseDataEntry[]): string {
	const rows = entries.map((entry) =>
		[
			entry.releaseId,
			entry.dates?.join(';') ?? '',
			entry.unknownCount ?? '',
			entry.favorite ?? '',
			entry.hidden ?? '',
			entry.hiddenUntil === null ? 'forever' : (entry.hiddenUntil ?? ''),
		].join(','),
	)
	const csv = `${RELEASE_DATA_HEADER}\n${rows.join('\n')}\n`
	if (
		entries.length > RELEASE_DATA_MAX_ROWS ||
		new TextEncoder().encode(csv).length > RELEASE_DATA_MAX_BYTES
	)
		throw new ReleaseDataError(
			'Release export exceeds the 10,000 row or 1 MB file limit.',
		)
	return csv
}
